/* eslint-disable react/destructuring-assignment */

import { Auth } from "aws-amplify";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { UserProps } from "../../domain/models/user";
import Authentication from "../../infra/cognito/authentication";
import LocalStorage from "../../infra/localStorage";
import { makeRemoteUser } from "../../main/factories/usecases/user/remote-user-factory";
import LocalStorageUtils from "../../utils/LocalStorageUtils";
import toastUtils from "../../utils/ToastUtils";
import { LoadingScreen } from "../../presentation/components/ui/loading";

const AuthContext: any = createContext(null);

export const AuthProviderGlobal = (props: any): JSX.Element => {
  const [user, setUser] = useState<UserProps | null>();
  const [reload, setReload] = useState<string>(uuidv4());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const history = useHistory();

  const setUserLocal = async (userParam: UserProps): Promise<void> => {
    await LocalStorage.set("user", userParam);
    const userData = await Authentication.getUserInfo();
    setUser(userData);
  };

  useEffect(() => {
    const handleLinkedInAuth = async (
      isAuthLinkedin: any,
      userCognito: any,
    ): Promise<void> => {
      LocalStorageUtils.setItem(isAuthLinkedin.user, "userRegister");
      LocalStorageUtils.setItem(JSON.stringify(userCognito), "cognitoUser");
      history.push("/cadastro");
    };

    const handleAuthorization = async (me: any): Promise<void> => {
      localStorage.setItem("username", me.email);
      const pathname = history?.location?.pathname;

      const isAdminAccessingNonAdminArea =
        !pathname.includes("/admin") && !!me.roles.find((it: any) => it.authority === "ADMIN");
      const isDevAccessingAdminArea =
        pathname.includes("/admin") && !!me.roles.find((it: any) => it.authority === "DEV");

      if (isAdminAccessingNonAdminArea) {
        toastUtils.showMessage({
          type: "error",
          message: "Você não tem acesso a essa área",
        });
        await Authentication.logout();
      } else if (isDevAccessingAdminArea) {
        toastUtils.showMessage({
          type: "error",
          message: "Você não tem acesso a essa área",
        });
        await Authentication.logout();
      }
    };

    const handlePostAuthRedirects = async (
      userCognito: any,
      isAuth: boolean,
      me: any,
    ): Promise<void> => {
      const pathname = history?.location?.pathname;
      const isNewUserAndAuthenticated = !user && isAuth && userCognito;
      const isNotEnabledDeveloper =
        isAuth && me && !me.developerResponseDTO?.enabled;

      const isNotAdmin = !me?.roles.find((it: any) => it?.authority === "ADMIN");
      const isSuperDev = !!me?.roles.find((it: any) => it?.authority === "SUPERADMIN");

      if (!user && isSuperDev) {
        await setUserLocal({
          name: userCognito?.attributes?.name || me?.name,
          email: userCognito?.attributes?.email,
          roles: me.roles,
          id: me.id,
          developerResponseDTO: me.developerResponseDTO,
        });
        history.push("/home");
        return;
      } if (isSuperDev) {
        history.push("/home");
        return;
      }

      if (isNewUserAndAuthenticated) {
        await setUserLocal({
          name: userCognito?.attributes?.name || me?.name,
          email: userCognito?.attributes?.email,
          roles: me.roles,
          id: me.id,
          developerResponseDTO: me.developerResponseDTO,
        });

        const isAdminPath = pathname === "/admin";
        const isHomePath = pathname === "/";

        if (isAdminPath) {
          history.push("/admin/home");
        } else if (isHomePath) {
          history.push("/home");
        }
      } else if (isNotEnabledDeveloper && isNotAdmin) {
        history.push("/cadastro");
      }
    };

    const checkAuthStatus = async (): Promise<void> => {
      try {
        const isAuthLinkedin = await Authentication.isAuthOnlyLinkedin();
        const isAuth = await Authentication.isAuth();
        const userCognito = await Auth.currentAuthenticatedUser();

        const isLinkedinUserDisabled =
          isAuthLinkedin?.user_isEnabled_application === "false";
        const isLinkedinUserNameContainsLinkedin =
          isAuthLinkedin?.["cognito:username"].includes("linkedin");

        if (
          isAuthLinkedin &&
          isLinkedinUserDisabled &&
          isLinkedinUserNameContainsLinkedin
        ) {
          await handleLinkedInAuth(isAuthLinkedin, userCognito);
        } else {
          const me = await makeRemoteUser().load();
          await handleAuthorization(me);
          await handlePostAuthRedirects(userCognito, isAuth, me);
        }
      } catch (error: any) {
        console.error("AUTH ERROR", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [reload, history, user]);

  const reloadAuth = (): void => setReload(uuidv4());

  return (
    <AuthContext.Provider value={{ reloadAuth, user, setUser, isLoading }}>
      {isLoading && (
        <LoadingScreen />
      )}
      {!isLoading && props.children}
    </AuthContext.Provider>
  );
};

export const useAuthGlobal = (): any => {
  const context: any = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a provider AuthProvider");
  }
  return { ...context };
};
